import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { EmptyState, ResourceMark, StatusBadge, directoryLinks, parseAdditionalNetworks, type ResourceMarkKey } from '@pazportal/ui';
import { supabase } from '../lib/supabase';
import { formatDate, formatDateTime, formatSolutionType, timeAgo } from '../lib/formatters';
import { propertyCodeOf } from '../hooks/usePropertyDirectory';
import { useUnifiSitesForLocation } from '../hooks/useUnifiSites';
import { parseUnifiCounts, unifiFreshness, unifiOnlineDevices } from '../lib/unifi';
import { usePlatformMetrics } from '../hooks/usePlatformMetrics';
import { OpenItemsPanel } from '../components/trips/OpenItemsPanel';
import { formatAvailabilityPct } from '../hooks/useCustomerNetworkLive';

interface LocationDetail {
  id: string;
  customer_id: string;
  name: string;
  type: string | null;
  address: { street?: string; city?: string; state?: string; zip?: string } | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  timezone: string | null;
  status: string | null;
  hierarchy: unknown;
  updated_at: string;
  customer: { id: string; name: string } | null;
  deployments: Array<{
    id: string;
    solution_type: string;
    vendor: string | null;
    status: string | null;
    commissioned_date: string | null;
  }>;
  tickets: Array<{
    id: string;
    ticket_number: string;
    title: string;
    status: string | null;
    priority: string | null;
    updated_at: string;
  }>;
}

interface PropertyLite {
  code: string;
  name: string;
  brand: string | null;
  unifi_host_id: string | null;
  mission_control_site_id: string | null;
  auvik_domain_prefix: string | null;
  learn_url: string | null;
  rankings_url: string | null;
  engineering_url: string | null;
  ordering_url: string | null;
  communications_vendor_url: string | null;
  additional_networks: unknown;
}

interface AuvikDeviceLite {
  id: string;
  name: string | null;
  device_type: string | null;
  status: string | null;
  ip_address: string | null;
  make_model: string | null;
  availability_pct: number | null;
  last_seen_at: string | null;
}

interface UptimeSampleLite {
  sampled_at: string;
  online_count: number;
  total_count: number;
  online_pct: number;
}

function useAdminLocation(id: string | undefined) {
  return useQuery({
    queryKey: ['admin-location', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select(`
          id, customer_id, name, type, address,
          contact_name, contact_phone, contact_email,
          timezone, status, hierarchy, updated_at,
          customer:customers!locations_customer_id_fkey(id, name),
          deployments(id, solution_type, vendor, status, commissioned_date),
          tickets(id, ticket_number, title, status, priority, updated_at)
        `)
        .eq('id', id!)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as LocationDetail | null;
    },
  });
}

function useProperty(code: string | null) {
  return useQuery({
    queryKey: ['property-directory', 'code', code],
    enabled: Boolean(code),
    queryFn: async (): Promise<PropertyLite | null> => {
      const { data, error } = await supabase
        .from('property_directory')
        .select('code, name, brand, unifi_host_id, mission_control_site_id, auvik_domain_prefix, learn_url, rankings_url, engineering_url, ordering_url, communications_vendor_url, additional_networks')
        .eq('code', code!)
        .maybeSingle();
      if (error) throw error;
      return (data as PropertyLite | null) ?? null;
    },
    staleTime: 60 * 1000,
  });
}

function useLocationDevices(locationId: string | undefined) {
  return useQuery({
    queryKey: ['admin-location-devices', locationId],
    enabled: Boolean(locationId),
    queryFn: async (): Promise<AuvikDeviceLite[]> => {
      const { data, error } = await supabase
        .from('devices')
        .select('id, name, device_type, status, ip_address, make_model, availability_pct, last_seen_at')
        .eq('location_id', locationId!)
        .order('name');
      if (error) throw error;
      return (data ?? []) as AuvikDeviceLite[];
    },
    staleTime: 60 * 1000,
  });
}

function useLatestUptimeSample(locationId: string | undefined) {
  return useQuery({
    queryKey: ['admin-location-uptime', locationId],
    enabled: Boolean(locationId),
    queryFn: async (): Promise<UptimeSampleLite | null> => {
      const { data, error } = await supabase
        .from('location_uptime_samples')
        .select('sampled_at, online_count, total_count, online_pct')
        .eq('location_id', locationId!)
        .order('sampled_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return { ...data, online_pct: Number(data.online_pct) };
    },
    staleTime: 60 * 1000,
  });
}

export function LocationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: location, isLoading, isError } = useAdminLocation(id);
  const propertyCode = propertyCodeOf(location?.hierarchy as never);
  const { data: property } = useProperty(propertyCode);
  const { data: unifiSites } = useUnifiSitesForLocation(id);
  const { data: devices } = useLocationDevices(id);
  const { data: uptime } = useLatestUptimeSample(id);
  const { data: metrics } = usePlatformMetrics();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-text-muted" role="status" aria-live="polite">
        <div className="mr-3 h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" aria-hidden="true" />
        Loading site…
      </div>
    );
  }

  if (isError || !location) {
    return (
      <EmptyState
        icon={<LocationIcon />}
        title="Site not found"
        subtitle="It may have been removed or you may not have access."
        action={{ label: 'Back to sites', onClick: () => navigate('/locations') }}
      />
    );
  }

  const address = formatAddress(location.address);
  const tickets = [...location.tickets].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  const auvikConnected = metrics?.auvik.connected ?? false;

  return (
    <div className="space-y-5">
      <div>
        <button type="button" onClick={() => navigate('/locations')} className="text-sm text-primary hover:underline min-h-[44px] sm:min-h-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded">
          ← Back to sites
        </button>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-text break-words">{location.name}</h1>
            <p className="mt-1 text-sm text-text-muted">{address || 'Address not provided'}</p>
            {location.customer && (
              <Link to={`/customers/${location.customer.id}`} className="mt-1 inline-block text-sm text-primary hover:underline">
                {location.customer.name}
              </Link>
            )}
          </div>
          <StatusBadge status={location.status ?? 'unknown'} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <section className="rounded-lg border border-border bg-surface p-5">
          <h2 className="font-semibold text-text">Site details</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <Fact label="Customer" value={location.customer?.name ?? 'Unmapped'} />
            <Fact label="Type" value={formatLabel(location.type)} />
            <Fact label="Time zone" value={location.timezone ?? '—'} />
            <Fact label="Last updated" value={formatDate(location.updated_at)} />
          </dl>
        </section>

        <section className="rounded-lg border border-border bg-surface p-5">
          <h2 className="font-semibold text-text">Property</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <Fact label="Property code" value={propertyCode ?? 'Not linked'} />
            <Fact label="Directory entry" value={property ? `${property.name}${property.brand ? ` (${property.brand})` : ''}` : propertyCode ? 'Code not found in directory' : '—'} />
            <Fact label="UniFi host id" value={property?.unifi_host_id ?? '—'} mono />
            <Fact label="Mission Control site" value={property?.mission_control_site_id ?? '—'} mono />
          </dl>
          <p className="mt-3 text-xs text-text-muted">
            {propertyCode ? (
              <Link to="/properties" className="text-primary hover:underline">Open the property directory</Link>
            ) : (
              <>No property code on this location. Add its name as an alias in the <Link to="/properties" className="text-primary hover:underline">property directory</Link> and relink.</>
            )}
          </p>
        </section>

        <section className="rounded-lg border border-border bg-surface p-5">
          <h2 className="font-semibold text-text">Site contact</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <Fact label="Name" value={location.contact_name ?? '—'} />
            <Fact label="Phone" value={location.contact_phone ?? '—'} />
            <Fact label="Email" value={location.contact_email ?? '—'} />
          </dl>
        </section>
      </div>

      <SiteResources property={property ?? null} propertyCode={propertyCode} locationId={location.id} />

      <section className="rounded-lg border border-border bg-surface">
        <div className="border-b border-border px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <h2 className="font-semibold text-text">UniFi sites ({unifiSites?.length ?? 0})</h2>
          <p className="text-xs text-text-muted">
            {uptime
              ? `Latest uptime sample ${timeAgo(uptime.sampled_at)}: ${uptime.online_count} of ${uptime.total_count} devices online (${uptime.online_pct.toFixed(1)}%)`
              : 'No uptime sample recorded for this site yet'}
          </p>
        </div>
        {(unifiSites?.length ?? 0) === 0 ? (
          <p className="px-5 py-6 text-sm text-text-muted">
            No UniFi site is mapped to this location. Map one on the <Link to="/integrations" className="text-primary hover:underline">Integrations page</Link>.
          </p>
        ) : (
          <ul className="divide-y divide-divider">
            {unifiSites!.map((site) => {
              const counts = parseUnifiCounts(site.attributes);
              const online = unifiOnlineDevices(counts);
              const freshness = unifiFreshness(site.updated_at);
              return (
                <li key={site.id} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text truncate">{site.site_name ?? 'Unnamed site'}</p>
                    <p className="text-xs text-text-muted break-words">
                      Host {site.unifi_host_name ?? site.unifi_host_id ?? '—'} · updated {timeAgo(site.updated_at)} ·{' '}
                      <span className={freshness === 'live' ? 'text-success' : freshness === 'stale' ? 'text-warning' : 'text-error'}>{freshness}</span>
                    </p>
                  </div>
                  <dl className="grid grid-cols-3 gap-3 text-center text-xs shrink-0">
                    <div>
                      <dt className="text-text-faint">Devices</dt>
                      <dd className="text-text font-medium">{counts ? `${online ?? '—'} / ${counts.totalDevice ?? '—'}` : '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-text-faint">Offline</dt>
                      <dd className={`font-medium ${(counts?.offlineDevice ?? 0) > 0 ? 'text-error' : 'text-text'}`}>{counts?.offlineDevice ?? '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-text-faint">Clients</dt>
                      <dd className="text-text font-medium">
                        {counts && (counts.wifiClient !== null || counts.wiredClient !== null)
                          ? (counts.wifiClient ?? 0) + (counts.wiredClient ?? 0)
                          : '—'}
                      </dd>
                    </div>
                  </dl>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-border bg-surface">
        <div className="border-b border-border px-5 py-4">
          <h2 className="font-semibold text-text">Auvik devices ({devices?.length ?? 0})</h2>
        </div>
        {(devices?.length ?? 0) === 0 ? (
          <p className="px-5 py-6 text-sm text-text-muted">
            {auvikConnected ? 'No Auvik device is assigned to this site.' : 'Auvik not connected: no device inventory has been received.'}
          </p>
        ) : (
          <ul className="divide-y divide-divider">
            {devices!.map((d) => (
              <li key={d.id} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text truncate">{d.name ?? 'Device'}</p>
                  <p className="text-xs text-text-muted break-words">
                    {[d.device_type, d.make_model, d.ip_address, d.availability_pct === null ? null : `${formatAvailabilityPct(d.availability_pct)} availability`, d.last_seen_at ? `seen ${timeAgo(d.last_seen_at)}` : null].filter(Boolean).join(' · ')}
                  </p>
                </div>
                {d.status && <StatusBadge status={d.status} />}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-border bg-surface p-5">
        <OpenItemsPanel locationId={location.id} />
      </section>

      <section className="rounded-lg border border-border bg-surface">
        <div className="border-b border-border px-5 py-4">
          <h2 className="font-semibold text-text">Deployments ({location.deployments.length})</h2>
        </div>
        {location.deployments.length === 0 ? (
          <p className="px-5 py-6 text-sm text-text-muted">No deployments recorded at this site.</p>
        ) : (
          <div className="divide-y divide-divider">
            {location.deployments.map((deployment) => (
              <div key={deployment.id} className="flex items-center justify-between gap-4 px-5 py-3">
                <div>
                  <p className="text-sm font-medium text-text">{formatSolutionType(deployment.solution_type)}</p>
                  <p className="text-xs text-text-muted">
                    {[deployment.vendor, deployment.commissioned_date ? `Commissioned ${formatDate(deployment.commissioned_date)}` : null]
                      .filter(Boolean)
                      .join(' · ') || 'No vendor or commission date'}
                  </p>
                </div>
                <StatusBadge status={deployment.status ?? 'unknown'} />
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-border bg-surface">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="font-semibold text-text">Tickets ({tickets.length})</h2>
          <Link to={`/locations/${location.id}/tickets`} className="text-sm text-primary hover:underline">View all</Link>
        </div>
        {tickets.length === 0 ? (
          <p className="px-5 py-6 text-sm text-text-muted">No tickets tied to this site.</p>
        ) : (
          <div className="divide-y divide-divider">
            {tickets.map((ticket) => (
              <Link
                key={ticket.id}
                to={`/locations/${location.id}/tickets/${ticket.id}`}
                className="flex items-start justify-between gap-4 px-5 py-3 hover:bg-surface-strong"
              >
                <div className="min-w-0">
                  <p className="text-xs font-mono text-text-faint">{ticket.ticket_number}</p>
                  <p className="truncate text-sm font-medium text-text">{ticket.title}</p>
                  <p className="text-xs text-text-muted">{formatDateTime(ticket.updated_at)}</p>
                </div>
                <div className="flex gap-2">
                  <StatusBadge status={ticket.priority ?? 'medium'} />
                  <StatusBadge status={ticket.status ?? 'open'} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Site resources — the same external links the customer portal's Site
 * Resources panel shows, with the same marks (`<ResourceMark>` from
 * @pazportal/ui). Rows come from `directoryLinks()` over the directory
 * entry; unlinked resources are listed so staff can see what is missing.
 */
function SiteResources({ property, propertyCode, locationId }: { property: PropertyLite | null; propertyCode: string | null; locationId: string }) {
  const links = directoryLinks(
    property ? { ...property, additional_networks: parseAdditionalNetworks(property.additional_networks) } : null,
  );
  type Row = { kind: ResourceMarkKey; title: string; description: string; href: string | null };
  const rows: Row[] = [
    { kind: 'missionControl', title: 'Network operations', description: 'Open this site’s network workspace in Portal', href: `/locations/${locationId}/network` },
    { kind: 'auvik', title: 'Auvik', description: 'Open this site in Auvik (network monitoring)', href: links.auvik },
    { kind: 'unifi', title: 'UniFi', description: "Open this site's UniFi console", href: links.unifi },
    { kind: 'communicationsVendor', title: 'Pinnacle Communications', description: 'Communications vendor website', href: links.communicationsVendor },
    { kind: 'rankings', title: 'Rankings', description: 'Guest-experience rankings', href: links.rankings },
    { kind: 'ordering', title: 'Wi-Q', description: 'Mobile ordering', href: links.ordering },
    { kind: 'learn', title: 'PazNet Learn', description: 'Property manuals and how-tos', href: links.learn },
    { kind: 'engineering', title: 'Engineering', description: 'Engineering & facilities reference', href: links.engineering },
    ...links.additionalNetworks.flatMap<Row>((n) => [
      { kind: 'missionControl', title: `${n.name} · Network operations`, description: 'Additional network in this site’s Portal workspace', href: n.missionControl ? `/locations/${locationId}/network` : null },
      { kind: 'unifi', title: `${n.name} · UniFi`, description: 'Additional network', href: n.unifi },
    ]),
  ];
  const linked = rows.filter((r) => r.href);
  const missing = rows.filter((r) => !r.href).map((r) => r.title);

  return (
    <section className="rounded-lg border border-border bg-surface" aria-labelledby="site-resources-heading">
      <div className="border-b border-border px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
        <h2 id="site-resources-heading" className="font-semibold text-text">Site resources ({linked.length})</h2>
        <p className="text-xs text-text-muted">External consoles and references for this property. Links open in a new tab.</p>
      </div>
      {!property ? (
        <p className="px-5 py-6 text-sm text-text-muted">
          {propertyCode
            ? 'Property code not found in the directory, so no resource links resolve.'
            : 'No property code on this location; link it in the property directory to get resource links.'}
        </p>
      ) : linked.length === 0 ? (
        <p className="px-5 py-6 text-sm text-text-muted">
          No resource ids or URLs on this directory entry yet. Add them in the <Link to="/properties" className="text-primary hover:underline">property directory</Link>.
        </p>
      ) : (
        <div className="p-5">
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {linked.map((row) => (
              <li key={row.title}>
                <a
                  href={row.href!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex min-h-11 items-center gap-3 rounded-md border border-border bg-surface-2 px-3 py-2.5 text-primary transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <ResourceMark kind={row.kind} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-text">{row.title}</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-text-muted">{row.description}</span>
                  </span>
                  <span aria-hidden="true" className="shrink-0">↗</span>
                  <span className="sr-only"> opens in a new tab</span>
                </a>
              </li>
            ))}
          </ul>
          {missing.length > 0 && (
            <p className="mt-3 text-xs text-text-muted">
              Not linked: {missing.join(', ')}. Add them in the <Link to="/properties" className="text-primary hover:underline">property directory</Link>.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-text-muted">{label}</dt>
      <dd className={`text-right text-text break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}

function formatAddress(address: LocationDetail['address']): string {
  if (!address) return '';
  return [
    address.street,
    [address.city, address.state].filter(Boolean).join(', '),
    address.zip,
  ].filter(Boolean).join(' ');
}

function formatLabel(value: string | null): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function LocationIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657 13.414 20.9a2 2 0 0 1-2.827 0l-4.244-4.243a8 8 0 1 1 11.314 0Z" />
    </svg>
  );
}
